import DOMPurify from "dompurify";

/**
 * HTML Sanitizer Utility
 *
 * Provides centralized HTML sanitization for rich text content (Quill editor)
 * to prevent XSS attacks while preserving legitimate formatting.
 *
 * This is the single source of truth for all HTML sanitization in the app.
 * Used by:
 * - RichTextEditor component (sanitize input/output)
 * - Notes API layer (sanitize before display)
 * - Any other rich text content
 *
 * Security Features:
 * - Blocks all script tags and event handlers
 * - Blocks dangerous CSS (expression, javascript:, etc.)
 * - Blocks inline SVG (can contain embedded scripts)
 * - Allows only safe base64 image types (PNG, JPEG, GIF, WebP, BMP)
 * - Blocks dangerous data URIs (text/html, text/javascript, etc.)
 * - Optionally adds security attributes to external links
 *
 * Preserved Formatting:
 * - Text formatting (bold, italic, underline, strikethrough)
 * - Headings (h1-h6)
 * - Lists (ordered, unordered)
 * - Links and images
 * - Colors and fonts (inline styles)
 * - Text alignment
 * - Blockquotes and code blocks
 */

/**
 * Allowed HTML tags for Quill rich text editor
 */
export const ALLOWED_TAGS = [
  "p",
  "br",
  "strong",
  "em",
  "u",
  "s", // strikethrough
  "a",
  "img",
  "ol",
  "ul",
  "li",
  "span",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "blockquote",
  "pre",
  "code",
] as const;

/**
 * Allowed HTML attributes for Quill rich text editor
 */
export const ALLOWED_ATTR = [
  "href",
  "src",
  "alt",
  "title",
  "class",
  "target",
  "rel",
  "style", // Required for inline styles (colors, fonts, alignment, etc.)
  "contenteditable", // Required by Quill mention blots on the inner span
] as const;

/**
 * Allowed data-* attributes used by Quill mention blots. data-* attributes
 * are inert with respect to XSS — they cannot trigger script execution — so
 * whitelisting the ones we use is safe.
 */
export const ALLOWED_DATA_ATTR = [
  "data-id",
  "data-value",
  "data-denotation-char",
  "data-index",
] as const;

/**
 * Forbidden HTML attributes (XSS vectors)
 */
export const FORBID_ATTR = [
  "onerror",
  "onload",
  "onclick",
  "onmouseover",
] as const;

/**
 * Forbidden HTML tags (XSS vectors)
 */
export const FORBID_TAGS = [
  "script",
  "iframe",
  "object",
  "embed",
  "form",
] as const;

/**
 * Allowed URI schemes for links and images
 */
export const ALLOWED_URI_REGEXP =
  /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i;

/**
 * Allowed CSS properties for inline styles
 * Quill uses inline styles for colors, fonts, alignment, etc.
 */
export const ALLOWED_STYLES = {
  "*": {
    color: [/^#[0-9a-fA-F]{3,6}$/, /^rgb\(/, /^rgba\(/],
    "background-color": [/^#[0-9a-fA-F]{3,6}$/, /^rgb\(/, /^rgba\(/],
    "font-size": [/^\d+(?:px|em|rem|%)$/],
    "font-family": [/.*/],
    "text-align": [/^(?:left|right|center|justify)$/],
    "text-decoration": [/^(?:none|underline|overline|line-through|blink)$/],
  },
} as const;

/**
 * Dangerous CSS patterns that can be used for XSS attacks
 */
export const DANGEROUS_CSS_PATTERNS = [
  /javascript:/gi,
  /expression\s*\(/gi,
  /import\s*\(/gi,
  /url\s*\(\s*javascript:/gi,
  /behavior:/gi,
  /-moz-binding:/gi,
] as const;

/**
 * Safe base64 image pattern
 * Allows: PNG, JPEG, JPG, GIF, WebP, BMP
 * Blocks: SVG (can contain embedded <script> tags)
 */
export const SAFE_IMAGE_PATTERN =
  /^data:image\/(png|jpeg|jpg|gif|webp|bmp);base64,/i;

/**
 * Options for HTML sanitization
 */
export interface ISanitizeOptions {
  /**
   * Whether to add security attributes to external links
   * (target="_blank", rel="noopener noreferrer")
   * @default false
   */
  securifyLinks?: boolean;
}

/**
 * Sanitizes HTML content to prevent XSS attacks while preserving Quill formatting
 *
 * This function uses DOMPurify with a strict configuration that:
 * - Allows only safe HTML tags and attributes used by Quill
 * - Blocks all script tags and event handlers
 * - Blocks dangerous CSS patterns (expression, javascript:, etc.)
 * - Allows safe base64 images (PNG, JPEG, GIF, WebP, BMP)
 * - Blocks inline SVG (can contain embedded scripts)
 * - Optionally adds security attributes to external links
 *
 * @param html - The HTML string to sanitize
 * @param options - Optional sanitization options
 * @returns Sanitized HTML string safe for display
 *
 * @example
 * ```typescript
 * // Basic usage
 * const safe = sanitizeHTML('<p>Hello <strong>World</strong></p>');
 *
 * // With link security
 * const safe = sanitizeHTML(
 *   '<a href="https://example.com">Link</a>',
 *   { securifyLinks: true }
 * );
 * // Returns: <a href="https://example.com" target="_blank" rel="noopener noreferrer">Link</a>
 *
 * // Blocks XSS
 * const safe = sanitizeHTML('<img src="x" onerror="alert(1)">');
 * // Returns: <img src="x"> (onerror removed)
 *
 * // Blocks SVG XSS
 * const safe = sanitizeHTML('<img src="data:image/svg+xml,<svg><script>alert(1)</script></svg>">');
 * // Returns: <img> (dangerous src removed)
 *
 * // Preserves safe base64 images
 * const safe = sanitizeHTML('<img src="data:image/png;base64,iVBORw0KGg...">');
 * // Returns: <img src="data:image/png;base64,iVBORw0KGg..."> (preserved)
 * ```
 */
export function sanitizeHTML(
  html: string,
  options: ISanitizeOptions = {}
): string {
  const { securifyLinks = false } = options;

  // Configure DOMPurify to only allow safe Quill formatting
  const config = {
    ALLOWED_TAGS: [...ALLOWED_TAGS],
    ALLOWED_ATTR: [...ALLOWED_ATTR, ...ALLOWED_DATA_ATTR],
    ALLOWED_URI_REGEXP,
    ALLOW_DATA_ATTR: false,
    KEEP_CONTENT: true,
    FORBID_ATTR: [...FORBID_ATTR],
    FORBID_TAGS: [...FORBID_TAGS],
    ALLOWED_STYLES,
  };

  // Add hook to sanitize attributes and block dangerous patterns
  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    // Securify external links (if enabled)
    if (securifyLinks && node.tagName === "A") {
      const href = node.getAttribute("href");
      if (href && !href.startsWith("#")) {
        node.setAttribute("target", "_blank");
        node.setAttribute("rel", "noopener noreferrer");
      }
    }

    // Remove dangerous data: URLs but allow safe base64 images
    if (node.tagName === "IMG") {
      const src = node.getAttribute("src");
      if (src && src.startsWith("data:")) {
        // Allow only base64-encoded safe image types
        // Block inline SVG (can contain <script> tags)
        if (!SAFE_IMAGE_PATTERN.test(src)) {
          node.removeAttribute("src");
        }
      }
    }

    // Sanitize style attribute to block dangerous CSS
    if (node.hasAttribute && node.hasAttribute("style")) {
      const style = node.getAttribute("style") || "";

      // Check for dangerous CSS patterns
      const hasDangerousPattern = DANGEROUS_CSS_PATTERNS.some((pattern) =>
        pattern.test(style)
      );

      if (hasDangerousPattern) {
        node.removeAttribute("style");
      }
    }
  });

  // Sanitize the HTML
  const sanitized = DOMPurify.sanitize(html, config);

  // Clean up hooks to prevent memory leaks
  DOMPurify.removeAllHooks();

  return sanitized;
}

/**
 * Alias for sanitizeHTML for backward compatibility
 * @deprecated Use sanitizeHTML instead
 */
export const cleanHTML = sanitizeHTML;
